/**
 * FloatingQueBoard - 퀴즈 문제 영역에서 플로팅 윈도우로 QueBoard를 띄우는 컴포넌트
 *
 * Windows 창 스타일: 드래그 이동, 리사이즈, 최소화, 닫기.
 * 수식 프리셋 네비게이션 지원 (복수 수식).
 */
import React, { useState, useRef, useCallback, useEffect } from 'react'
import { QueBoard } from './QueBoard'
import { STAGE_WIDTH, STAGE_HEIGHT } from './constants'
import type { Problem, QueBoardConfig } from './types'

interface FloatingQueBoardProps {
  problems: Problem[]
  onClose: () => void
}

const TITLE_BAR_HEIGHT = 36
const NAV_BAR_HEIGHT = 36
const ASPECT_RATIO = STAGE_WIDTH / STAGE_HEIGHT

const SIZE_PRESETS = [
  { label: 'S', width: 420 },
  { label: 'M', width: 560 },
  { label: 'L', width: 740 },
] as const

/** 너비 기준으로 전체 윈도우 높이 계산 */
function calcHeight(width: number, hasNav: boolean): number {
  const boardHeight = width / ASPECT_RATIO
  return Math.round(TITLE_BAR_HEIGHT + boardHeight + (hasNav ? NAV_BAR_HEIGHT : 0))
}

export const FloatingQueBoard: React.FC<FloatingQueBoardProps> = ({ problems, onClose }) => {
  const [problemIndex, setProblemIndex] = useState(0)
  const [minimized, setMinimized] = useState(false)
  const [sizeIndex, setSizeIndex] = useState(1) // default M
  const hasNav = problems.length > 1

  // Position & size (height auto-calculated from width)
  const width = SIZE_PRESETS[sizeIndex].width
  const height = calcHeight(width, hasNav)
  const [pos, setPos] = useState({ x: window.innerWidth - SIZE_PRESETS[1].width - 24, y: window.innerHeight - calcHeight(SIZE_PRESETS[1].width, hasNav) - 24 })

  // Drag state
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null)

  const currentProblem = problems[problemIndex]

  const config: QueBoardConfig = {
    problem: currentProblem,
    allowCustomInput: true,
    features: {
      tie: true,
      teleport5: true,
      teleport10: true,
      bank: true,
      indicator: true,
      customInput: true,
    },
    tutorial: { enabled: false, modules: [], autoStart: false },
    character: 'random',
  }

  // ── Drag handlers ──────────────────────────────────────────────
  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: pos.x, startPosY: pos.y }

    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return
      const dx = ev.clientX - dragRef.current.startX
      const dy = ev.clientY - dragRef.current.startY
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 100, dragRef.current.startPosX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - 40, dragRef.current.startPosY + dy)),
      })
    }
    const onUp = () => {
      dragRef.current = null
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [pos])

  // Constrain position on window resize
  useEffect(() => {
    const handler = () => {
      setPos(prev => ({
        x: Math.min(prev.x, window.innerWidth - 100),
        y: Math.min(prev.y, window.innerHeight - 40),
      }))
    }
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  // ── Minimized state ────────────────────────────────────────────
  if (minimized) {
    return (
      <div
        className="fixed z-200 bottom-4 right-4 bg-indigo-600 text-white rounded-lg shadow-xl px-4 py-2 cursor-pointer flex items-center gap-2 hover:bg-indigo-700 transition-colors"
        onClick={() => setMinimized(false)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
        <span className="text-sm font-medium">깨보드 {currentProblem.left} x {currentProblem.right}</span>
      </div>
    )
  }

  return (
    <div
      className="fixed z-200 flex flex-col bg-white rounded-xl shadow-2xl border border-gray-300 overflow-hidden"
      style={{
        left: pos.x,
        top: pos.y,
        width,
        height,
      }}
    >
      {/* Title bar */}
      <div
        className="flex items-center justify-between px-3 bg-linear-to-r from-indigo-600 to-indigo-500 rounded-t-xl cursor-move select-none shrink-0"
        style={{ height: TITLE_BAR_HEIGHT }}
        onMouseDown={onDragStart}
      >
        <div className="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
          <span className="text-white text-sm font-medium">깨보드</span>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Size presets */}
          <div className="flex items-center gap-0.5 mr-1">
            {SIZE_PRESETS.map((preset, i) => (
              <button
                key={preset.label}
                onClick={(e) => { e.stopPropagation(); setSizeIndex(i) }}
                className={`w-6 h-5 rounded text-[10px] font-bold flex items-center justify-center transition-colors ${
                  sizeIndex === i
                    ? 'bg-white/30 text-white'
                    : 'text-white/60 hover:bg-white/15 hover:text-white/90'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {/* Minimize */}
          <button
            onClick={(e) => { e.stopPropagation(); setMinimized(true) }}
            className="w-6 h-6 rounded-xs hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 12 12"><line x1="2" y1="6" x2="10" y2="6" stroke="white" strokeWidth="1.5" /></svg>
          </button>
          {/* Close */}
          <button
            onClick={(e) => { e.stopPropagation(); onClose() }}
            className="w-6 h-6 rounded-xs hover:bg-red-500/80 flex items-center justify-center transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 12 12"><line x1="2" y1="2" x2="10" y2="10" stroke="white" strokeWidth="1.5" /><line x1="10" y1="2" x2="2" y2="10" stroke="white" strokeWidth="1.5" /></svg>
          </button>
        </div>
      </div>

      {/* QueBoard content */}
      <div className="flex-1 overflow-hidden">
        <QueBoard
          isOpen={true}
          onClose={onClose}
          config={config}
          noTopRadius
        />
      </div>

      {/* Preset navigator (복수 수식일 때만) */}
      {hasNav && (
        <div
          className="flex items-center justify-center gap-3 px-3 bg-gray-100 border-t border-gray-200 shrink-0"
          style={{ height: NAV_BAR_HEIGHT }}
        >
          <button
            onClick={() => setProblemIndex((prev) => (prev - 1 + problems.length) % problems.length)}
            className="p-1 rounded-xs hover:bg-gray-200 text-gray-600"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <span className="text-sm font-medium text-gray-700">
            {currentProblem.left} x {currentProblem.right}
            <span className="text-gray-400 ml-2">({problemIndex + 1}/{problems.length})</span>
          </span>
          <button
            onClick={() => setProblemIndex((prev) => (prev + 1) % problems.length)}
            className="p-1 rounded-xs hover:bg-gray-200 text-gray-600"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      )}

    </div>
  )
}
